import { getBffBaseUrl, getVisitKey, GUESTS_PATH } from '@/lib/bff/config'
import type { Pagination } from '@/lib/bff/people'

/*
 * BFF client for the guests of one tenant (VIS-460): search, the three guest
 * lists, one guest record and its history. Every call carries the tenant.
 * The BFF has already left out contact fields without
 * `guests.contact_details.view` and diagnostic fields without
 * `guests.diagnostics.view`, so the SPA shows what arrives. An unknown guest,
 * another tenant's guest and a switched-off guest record all answer
 * `404 not_found`. Errors arrive in the `{ error, problems? }` envelope.
 *
 * Times: `GuestActivity.at` and a table item's `at` are tenant-local
 * date-times with no offset; message, not-sent and order times and
 * `lastTried` are UTC instants; membership dates are calendar dates.
 *
 * Opening a record, and a Landlord's list or search, is recorded before
 * anything is returned (VIS-330): `503 access_not_recorded` means it couldn't
 * be, and nothing was returned.
 */

export type GuestListId = 'arrivals' | 'failed-messages' | 'shadow-guests'
export const GUEST_LISTS: GuestListId[] = ['arrivals', 'failed-messages', 'shadow-guests']

export type GuestSource = 'memberships' | 'orders'
export type GuestNotice = 'partial_association' | 'unattributed_messages' | 'history_limited'
export type GuestChannel = 'sms' | 'email'
export type GuestMessageKind =
  | 'visit_reminder'
  | 'post_show_moment'
  | 'tickets_revealed'
  | 'booking'
  | 'custom'
  | 'transfer'
  | 'broadcast'
  | 'personal'
  | 'campaign'
  | 'otp'
  | 'other'
export type GuestPartyRole = 'booker' | 'shadow_guest' | 'share_not_accepted'
export type NotSentReason =
  | 'opted_out'
  | 'no_number'
  | 'no_address'
  | 'not_sent'
  | 'disabled'
  | 'delivery_method_not_matched'
  | 'could_not_send'

export type GuestName = { firstName: string; lastName: string }
/** `startDate` and `endDate` are `YYYY-MM-DD`. */
export type GuestMembership = { name: string; startDate: string; endDate: string }
/** A performance; `at` is tenant-local with no offset. */
export type GuestActivity = { visitId: string; at?: string; title?: string; venueName?: string }
export type GuestContact = { email?: string; mobile?: string }

export type GuestSummary = {
  guestId: string
  name: GuestName
  customerNumber?: string
  currentMemberships: GuestMembership[]
  latestActivity?: GuestActivity
  contact?: GuestContact
}

export type GuestSearchResult = { data: GuestSummary[]; more: boolean; unavailable: GuestSource[]; timeZone: string }

export type GuestListRow = GuestSummary & {
  arriving?: GuestActivity
  kind?: GuestMessageKind
  channel?: GuestChannel
  /** UTC instant. */
  lastTried?: string
}

export type GuestVenue = { id: string; name: string }

export type GuestListPage = {
  list: GuestListId
  data: GuestListRow[]
  pagination: Pagination
  /** The `all` sentinel first, then each venue. */
  venues: GuestVenue[]
  timeZone: string
  unavailable: GuestSource[]
  /** The tenant's today, "YYYY-MM-DD". */
  today: string
  /** Arrivals only: the tenant calendar day shown, "YYYY-MM-DD". */
  date?: string
}

export type GuestSharer = { guestId?: string; name: GuestName }

export type GuestProfile = {
  guestId: string
  name: GuestName
  customerNumber?: string
  contact?: GuestContact
  currentMemberships: GuestMembership[]
  sharedBy: GuestSharer[]
}

/** `viewId` names the recorded view of this record (VIS-330); history passes it as `view`. */
export type GuestRecord = {
  profile: GuestProfile
  notices: GuestNotice[]
  unavailable: GuestSource[]
  timeZone: string
  viewId: string
}

export type GuestPartyMember = { guestId?: string; role: GuestPartyRole; name: GuestName }

export type GuestDeliveryAttempt = { status: string | null; at: string | null }

export type GuestDiagnostic = {
  status?: string
  stage?: string
  providerMessageId?: string
  attempts?: GuestDeliveryAttempt[]
  entryType?: string
  batchId?: string
  orderId?: string
  text?: string
}

export type GuestHistoryItem = {
  type: 'message' | 'not_sent' | 'table' | 'order'
  channel?: GuestChannel
  kind?: GuestMessageKind
  outcome?: 'delivered' | 'sent' | 'failed' | 'not_sent' | 'could_not_send'
  at?: string
  outcomeAt?: string
  resolvedLater?: boolean
  reason?: NotSentReason
  partySize?: number
  cancelled?: boolean
  bookingReference?: string
  orderTypeName?: string
  locationName?: string
  status?: string
  itemCount?: number
  total?: number
  diagnostic?: GuestDiagnostic
}

export type GuestHistoryVisit = {
  visitId: string
  role: GuestPartyRole
  performance: GuestActivity
  party: GuestPartyMember[]
  items: GuestHistoryItem[]
  notices: GuestNotice[]
  unavailable: GuestSource[]
}

export type GuestHistoryPage = { data: GuestHistoryVisit[]; next?: string; notices: GuestNotice[]; timeZone: string }

export type InputProblem = { field: string; code: string }

/** Raised for a non-2xx response or a network failure, with the BFF's error code and problems when sent. */
export class GuestsRequestError extends Error {
  readonly status: number
  readonly code: string | null
  readonly problems: InputProblem[]

  constructor(status: number, code: string | null, problems: InputProblem[] = [], message = `Guests request failed (${status})`) {
    super(message)
    this.name = 'GuestsRequestError'
    this.status = status
    this.code = code
    this.problems = problems
  }
}

function headers(token: string, json = false): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'x-visit-key': getVisitKey(),
    Accept: 'application/json',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  }
}

function url(tenantId: string, path: string, query: Record<string, string> = {}) {
  const search = new URLSearchParams({ tenant: tenantId, ...query })
  return `${getBffBaseUrl('mmv')}${GUESTS_PATH}${path}?${search}`
}

async function send<T>(input: string, init: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(input, init)
  } catch {
    throw new GuestsRequestError(0, null, [], 'Network error during a Guests request')
  }
  if (!response.ok) {
    let code: string | null = null
    let problems: InputProblem[] = []
    try {
      const body = (await response.json()) as { error?: unknown; problems?: unknown }
      if (typeof body?.error === 'string') code = body.error
      if (Array.isArray(body?.problems)) problems = body.problems as InputProblem[]
    } catch {
      code = null
    }
    throw new GuestsRequestError(response.status, code, problems)
  }
  try {
    return (await response.json()) as T
  } catch {
    throw new GuestsRequestError(response.status, null, [], 'Malformed Guests response')
  }
}

/** Up to 25 guests matching `q`. The term travels in the body, never in a URL. */
export function searchGuests(token: string, tenantId: string, q: string) {
  return send<GuestSearchResult>(url(tenantId, '/search'), {
    method: 'POST',
    headers: headers(token, true),
    body: JSON.stringify({ q }),
  })
}

/**
 * One 0-based page of a guest list. For Arrivals, `venue` narrows to a venue
 * (every venue by default) and `date` is the tenant calendar day,
 * "YYYY-MM-DD" (the tenant's today by default).
 */
export function listGuests(
  token: string,
  tenantId: string,
  list: GuestListId,
  page: number,
  { venue, date }: { venue?: string; date?: string } = {},
) {
  const query: Record<string, string> = { page: String(page) }
  if (venue && venue !== 'all') query.venue = venue
  if (date) query.date = date
  return send<GuestListPage>(url(tenantId, `/lists/${list}`, query), { method: 'GET', headers: headers(token) })
}

export function getGuestRecord(token: string, tenantId: string, guestId: string) {
  return send<GuestRecord>(url(tenantId, `/${encodeURIComponent(guestId)}`), { method: 'GET', headers: headers(token) })
}

/**
 * One page of the guest's visits, newest first; `before` is the previous
 * page's `next`. `view` is the record's `viewId`, so the BFF knows this view
 * is already recorded (VIS-330).
 */
export function getGuestHistory(token: string, tenantId: string, guestId: string, before?: string, view?: string) {
  const query: Record<string, string> = {}
  if (before) query.before = before
  if (view) query.view = view
  return send<GuestHistoryPage>(url(tenantId, `/${encodeURIComponent(guestId)}/history`, query), {
    method: 'GET',
    headers: headers(token),
  })
}

export const isNotFound = (error: unknown) => error instanceof GuestsRequestError && error.status === 404
export const isForbidden = (error: unknown) => error instanceof GuestsRequestError && error.status === 403
